import { css } from '@emotion/react'
import { Cell, Grid, Heading, InfoLabel, Text, VFlow } from 'bold-ui'
import { Breadcrumb } from 'components/breadcrumb'
import { PageContent } from 'components/layout/PageContent'
import { LotacaoResponsavelCard } from 'components/lotacao-responsavel/LotacaoResponsavelCard'
import { TitleGroup } from 'components/TitleGroup'
import { Fragment } from 'react'
import { Redirect } from 'react-router'
import { AtividadeColetivaInfoHeader } from 'view/atividade-coletiva/componentes/AtividadeColetivaInfoHeader'
import { LotacoesEnvolvidasTable } from 'view/atividade-coletiva/componentes/LotacoesEnvolvidasTable'
import { ObservacaoCidadaoTable } from 'view/atividade-coletiva/componentes/observacao-cidadao/ObservacaoCidadaoTable'
import { ParticipantesTable } from 'view/atividade-coletiva/componentes/participantes/ParticipantesTable'
import {
  AtividadeColetivaReuniaoModel,
  ParticipanteDataTableColumnEnum,
  temaReuniaoRecord,
  temaSaudeRecord,
  tipoAtividadeRecord,
  TIPOS_ATIVIDADE_REUNIAO,
} from 'view/atividade-coletiva/model-atividadeColetiva'

interface AtividadeColetivaVisualizarReuniaoViewProps {
  atividadeColetiva: AtividadeColetivaReuniaoModel
  isFonteDadosPec: boolean
}

export const AtividadeColetivaVisualizarReuniaoView = (props: AtividadeColetivaVisualizarReuniaoViewProps) => {
  const { atividadeColetiva, isFonteDadosPec } = props
  const anotacoesTextOnly = atividadeColetiva?.anotacoes?.removeTags()

  if (!TIPOS_ATIVIDADE_REUNIAO.includes(atividadeColetiva.tipoAtividade)) {
    return <Redirect to='/404' />
  }

  return (
    <Fragment>
      <Breadcrumb title='Visualização de registro de reunião de equipe' />
      <PageContent type='filled' style={styles.pageContent}>
        <TitleGroup title='Registro de reunião' style={styles.header} />

        <Grid gap={2}>
          <Cell size={12}>
            <VFlow vSpacing={0.5}>
              <Heading level={3}>Responsabilidade de registro</Heading>
              <LotacaoResponsavelCard responsavel={atividadeColetiva.lotacaoResponsavel} />
            </VFlow>
          </Cell>
          <Cell size={12}>
            <AtividadeColetivaInfoHeader atividadeColetiva={atividadeColetiva} isFonteDadosPec={isFonteDadosPec} />
          </Cell>
          <Cell size={12} style={styles.section}>
            <Heading level={2} style={styles.title}>
              Profissionais envolvidos
            </Heading>
          </Cell>
          <Cell size={12}>
            <LotacoesEnvolvidasTable items={atividadeColetiva.lotacoesEnvolvidas ?? []} />
          </Cell>
          <Cell size={12} style={styles.section}>
            <Heading level={2} style={styles.title}>
              Dados da atividade
            </Heading>
          </Cell>
          <Cell size={4}>
            <InfoLabel title='Tipo da atividade'>{tipoAtividadeRecord[atividadeColetiva.tipoAtividade]}</InfoLabel>
          </Cell>
          <Cell size={4}>
            <InfoLabel title='Temas da reunião'>
              {atividadeColetiva.temasReuniao
                ?.map((tema) => temaReuniaoRecord[tema])
                .sort((a, b) => a.localeCompare(b))
                .join(', ')}
            </InfoLabel>
          </Cell>
          <Cell size={4}>
            <InfoLabel title='Temas para a saúde'>
              {atividadeColetiva.temasSaude
                ?.map((tema) => temaSaudeRecord[tema])
                .sort((a, b) => a.localeCompare(b))
                .join(', ')}
            </InfoLabel>
          </Cell>
          {isFonteDadosPec && (
            <Fragment>
              <Cell size={12} style={styles.wordBreakSection}>
                <InfoLabel title='Anotações da atividade'>
                  {anotacoesTextOnly && <Text dangerouslySetInnerHTML={{ __html: atividadeColetiva.anotacoes }} />}
                </InfoLabel>
              </Cell>
              <Cell size={12} style={styles.section}>
                <Heading level={2} style={styles.title}>
                  Observações sobre cidadão
                </Heading>
                <ObservacaoCidadaoTable items={atividadeColetiva.observacoes ?? []} />
              </Cell>
            </Fragment>
          )}
          {!isFonteDadosPec && (
            <Fragment>
              <Cell size={12} style={styles.section}>
                <Heading level={2} style={styles.title}>
                  Participantes
                </Heading>
              </Cell>
              <Cell size={12}>
                <ParticipantesTable
                  items={atividadeColetiva.participantes ?? []}
                  dataAtividade={atividadeColetiva.dataAtividade}
                  isPraticaSaudePnct={false}
                  visibleColumns={[ParticipanteDataTableColumnEnum.NOME]}
                />
              </Cell>
            </Fragment>
          )}
        </Grid>
      </PageContent>
    </Fragment>
  )
}

const styles = {
  header: css`
    margin-top: 1rem;
    margin-bottom: 2rem;
  `,
  section: css`
    margin-top: 1.5rem;
  `,
  title: css`
    margin-bottom: 0.5rem;
  `,
  pageContent: css`
    padding-bottom: 3.5rem;
  `,
  wordBreakSection: css`
    word-break: break-all;
  `,
}
